import React, { useState } from 'react';
import useSiteRequests from './useSiteRequests';

/**
 * SiteRequestsPanel
 *
 * Staff component. Drop this anywhere on a staff-only page, e.g. inside
 * AdvicePasswordManagement or its own route.
 *
 * Props:
 *   onSitesChanged {fn} — called after approve/deny so the parent can refetch
 *                         the sites list and update dropdowns immediately.
 */
export default function SiteRequestsPanel({ onSitesChanged }) {
  const [statusFilter, setStatusFilter] = useState('pending');
  const [denyModalId, setDenyModalId] = useState(null);
  const [denyReason, setDenyReason] = useState('');

  const {
    requests,
    loading,
    error,
    toastMessage,
    approveRequest,
    denyRequest,
    deleteRequest,
    refetch,
  } = useSiteRequests({ mine: false, status: statusFilter });

  const handleApprove = async (id) => {
    await approveRequest(id);
    onSitesChanged?.();
  };

  const handleDenySubmit = async () => {
    if (!denyModalId) return;
    await denyRequest(denyModalId, denyReason);
    setDenyModalId(null);
    setDenyReason('');
    onSitesChanged?.();
  };

  const pendingCount = requests.filter((r) => r.status === 'pending').length;

  return (
    <div className="card mb-3">
      <div className="card-header d-flex align-items-center justify-content-between py-2">
        <div className="d-flex align-items-center gap-2">
          <span style={{ fontWeight: 600, fontSize: '14px' }}>
            Site Requests
          </span>
          {statusFilter === 'pending' && pendingCount > 0 && (
            <span className="badge bg-warning text-dark">{pendingCount} pending</span>
          )}
        </div>

        <div className="d-flex align-items-center gap-2">
          {['pending', 'approved', 'denied'].map((s) => (
            <button
              key={s}
              className={`btn btn-sm ${statusFilter === s ? 'btn-primary' : 'btn-outline-secondary'}`}
              style={{ fontSize: '12px', padding: '2px 10px', textTransform: 'capitalize' }}
              onClick={() => setStatusFilter(s)}
            >
              {s}
            </button>
          ))}
          <button
            className="btn btn-outline-secondary btn-sm"
            style={{ fontSize: '12px' }}
            onClick={refetch}
            title="Refresh"
          >
            ↻
          </button>
        </div>
      </div>

      <div className="card-body p-0">
        {toastMessage && (
          <div className="alert alert-info alert-dismissible m-2 py-1 px-3 mb-0" style={{ fontSize: '13px' }}>
            {toastMessage}
          </div>
        )}

        {loading && (
          <div className="p-3 text-muted small">Loading…</div>
        )}
        {error && (
          <div className="p-3 text-danger small">{error}</div>
        )}

        {!loading && requests.length === 0 && (
          <div className="p-3 text-muted small">
            No {statusFilter} requests.
          </div>
        )}

        {!loading && requests.length > 0 && (
          <table className="table table-sm table-hover mb-0" style={{ fontSize: '13px' }}>
            <thead className="table-light">
              <tr>
                <th>Site</th>
                <th>Requested by</th>
                <th>Submitted</th>
                {statusFilter !== 'pending' && <th>Reviewed by</th>}
                {statusFilter === 'denied' && <th>Reason</th>}
                <th style={{ width: '160px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => (
                <tr key={req.id}>
                  <td>
                    <strong>{req.site}</strong>
                  </td>
                  <td>{req.requested_by}</td>
                  <td style={{ color: '#6c757d' }}>
                    {new Date(req.created_at).toLocaleDateString('en-GB', {
                      day: '2-digit', month: 'short', year: 'numeric',
                    })}
                  </td>
                  {statusFilter !== 'pending' && (
                    <td>{req.reviewed_by || '—'}</td>
                  )}
                  {statusFilter === 'denied' && (
                    <td style={{ color: '#6c757d' }}>{req.deny_reason || '—'}</td>
                  )}
                  <td>
                    {req.status === 'pending' && (
                      <div className="d-flex gap-1">
                        <button
                          className="btn btn-success btn-sm"
                          style={{ fontSize: '11px', padding: '2px 10px' }}
                          onClick={() => handleApprove(req.id)}
                        >
                          ✓ Approve
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          style={{ fontSize: '11px', padding: '2px 10px' }}
                          onClick={() => {
                            setDenyModalId(req.id);
                            setDenyReason('');
                          }}
                        >
                          ✗ Deny
                        </button>
                      </div>
                    )}
                    {req.status !== 'pending' && (
                      <button
                        className="btn btn-outline-danger btn-sm"
                        style={{ fontSize: '11px', padding: '2px 8px' }}
                        onClick={() => deleteRequest(req.id)}
                        title="Hard-delete this request"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {denyModalId && (
        <>
          <div
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
              zIndex: 10000,
            }}
            onClick={() => setDenyModalId(null)}
          />
          <div
            style={{
              position: 'fixed', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              background: '#fff', borderRadius: 8, padding: 24, width: 360,
              zIndex: 10001, boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            }}
          >
            <h6 className="mb-3">Deny request</h6>
            <label className="form-label small text-muted">
              Reason (optional — shown to requester)
            </label>
            <input
              type="text"
              className="form-control form-control-sm mb-3"
              placeholder="e.g. duplicate of existing site"
              value={denyReason}
              onChange={(e) => setDenyReason(e.target.value)}
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleDenySubmit()}
            />
            <div className="d-flex gap-2 justify-content-end">
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setDenyModalId(null)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger btn-sm"
                onClick={handleDenySubmit}
              >
                Confirm Deny
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
